// src/pages/Auth.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  School,
  ArrowForward,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { meService, IS_REDEPROG } from '../services/api';

const ECOLAB_API_URL =
  process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';

let _siteKeyPromise = null;
function getSiteKey() {
  if (!_siteKeyPromise) {
    _siteKeyPromise = fetch(`${ECOLAB_API_URL}/api/ecolab/auth/turnstile-sitekey`)
      .then(r => r.json()).then(d => d.siteKey || '').catch(() => '');
  }
  return _siteKeyPromise;
}

function waitForTurnstile(retries = 50) {
  return new Promise((resolve, reject) => {
    const check = (n) => {
      if (window.turnstile) return resolve();
      if (n <= 0) return reject(new Error('Turnstile no cargó'));
      setTimeout(() => check(n - 1), 100);
    };
    check(retries);
  });
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { productoInfo } = useAppTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tsToken, setTsToken]   = useState('');

  const containerRef = useRef(null);
  const widgetIdRef  = useRef(null);

  const explicitFrom = location.state?.from?.pathname || location.state?.from || null;

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [siteKey] = await Promise.all([getSiteKey(), waitForTurnstile()]);
        if (cancelado || !siteKey) { return; }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'login',
          callback: (token) => { if (!cancelado) { setTsToken(token); } },
          'error-callback': () => {},
          'expired-callback': () => {
            if (!cancelado) {
              setTsToken('');
              try { window.turnstile.reset(widgetIdRef.current); } catch {}
            }
          },
        });
      } catch {}
    })();
    return () => {
      cancelado = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!tsToken) { setError('Espere a que se complete la verificación de seguridad.'); return; }
    setIsLoading(true);
    const result = await login(email, password, tsToken);
    setIsLoading(false);
    if (result.success) {
      // Si venía de una ruta específica (lo mandaron a loguearse desde
      // ahí), respeta eso. Si no, y pertenece a alguna institución
      // (Rector, SuperAdmin, Coordinador...), lo llevamos directo al
      // portal de su institución en vez del dashboard genérico de
      // estudiante -- así lo pidió el flujo de alianzas educativas.
      if (explicitFrom) {
        navigate(explicitFrom, { replace: true });
        return;
      }
      try {
        const misInstituciones = await meService.getMisInstituciones();
        if (misInstituciones && misInstituciones.length > 0) {
          navigate('/mi-institucion', { replace: true });
          return;
        }
      } catch {
        // Si falla la consulta, no bloquea el login -- sigue al dashboard normal.
      }
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error || 'Credenciales inválidas');
      // Regenerar token para el siguiente intento
      setTsToken('');
      try { window.turnstile?.reset(widgetIdRef.current); } catch {}
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 4,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #e94560, #ff6b6b)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <School sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {productoInfo.etiquetaLogo ? (
              <>
                {productoInfo.etiquetaLogo.prefijo}
                <span style={{ color: '#e94560' }}>{productoInfo.etiquetaLogo.raiz}</span>
              </>
            ) : (
              <span style={{ color: '#e94560' }}>{productoInfo.nombre}</span>
            )}
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
              Bienvenido de nuevo
            </Typography>
            <Typography color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              {IS_REDEPROG ? 'Ingresa con tu correo y contraseña' : 'Ingresa con tus credenciales del ecosistema Ecolab'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Email color="disabled" /></InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Lock color="disabled" /></InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Contenedor Turnstile (modo Managed - visible) */}
              <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }} />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || !tsToken}
                endIcon={<ArrowForward />}
              >
                {isLoading ? 'Ingresando...' : 'Iniciar sesión'}
              </Button>
            </Box>

            <Typography textAlign="center" sx={{ mt: 3 }} color="text.secondary">
              ¿Aún no tienes cuenta?{' '}
              <Link to="/register" style={{ color: '#e94560', fontWeight: 600 }}>
                Regístrate
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export function RegisterPage() {
  // El registro de usuarios YA NO vive en EducaT — el ecosistema Ecolab
  // es la fuente única de identidad. Cualquier persona con cuenta Ecolab
  // que inicia sesión en EducaT queda aprovisionada automáticamente con
  // rol Estudiante (EnsureEducaTUserFilter, backend). Si necesita cuenta
  // Ecolab, se crea allá.
  const ECOLAB_REGISTER_URL =
    process.env.REACT_APP_ECOLAB_REGISTER_URL || 'https://ecolab-oficial.org/registro';
  const { productoInfo } = useAppTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 4,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #e94560, #ff6b6b)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <School sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {productoInfo.etiquetaLogo ? (
              <>
                {productoInfo.etiquetaLogo.prefijo}
                <span style={{ color: '#e94560' }}>{productoInfo.etiquetaLogo.raiz}</span>
              </>
            ) : (
              <span style={{ color: '#e94560' }}>{productoInfo.nombre}</span>
            )}
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
              Crea tu cuenta
            </Typography>
            <Typography color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              {productoInfo.nombre} forma parte de la red solidaria Ecolab. Tu cuenta se crea
              una sola vez en Ecolab y sirve para toda la red — incluido {productoInfo.nombre}.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Al registrarte en Ecolab y luego iniciar sesión aquí, tu cuenta
              queda habilitada automáticamente en {productoInfo.nombre} con rol de estudiante.
            </Alert>

            <Button
              variant="contained"
              fullWidth
              size="large"
              endIcon={<ArrowForward />}
              href={ECOLAB_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Registrarme en Ecolab
            </Button>

            <Typography textAlign="center" sx={{ mt: 3 }} color="text.secondary">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" style={{ color: '#e94560', fontWeight: 600 }}>
                Inicia sesión
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
